import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    CheckCircleIcon,
    XMarkIcon,
    ClockIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    EyeIcon,
    AcademicCapIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { API_BASE as API } from '../../lib/config';

const STATUS_LABELS = {
    pending: '確認中', // API default is 'pending'
    approved: '承認済',
    rejected: '再提出要'
};

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
};

const AdminQualificationsPage = () => {
    const { user } = useAuth();
    const [qualifications, setQualifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQualification, setSelectedQualification] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
            };

            const params = new URLSearchParams();
            if (filterStatus) {
                params.append('status', filterStatus);
            }

            const response = await axios.get(`${API}/admin/qualifications?${params}`, { headers });
            setQualifications(response.data);
        } catch (error) {
            console.error('Failed to fetch qualifications:', error);
            toast.error('データの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleReview = async (status) => {
        if (!selectedQualification) return;

        try {
            const headers = {
                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
            };

            await axios.put(`${API}/admin/qualifications/${selectedQualification.id}`, {
                status,
                rejection_reason: status === 'rejected' ? rejectionReason : null
            }, { headers });

            toast.success(status === 'approved' ? '資格を承認しました' : '資格を差し戻しました');
            setShowModal(false);
            setSelectedQualification(null);
            fetchData();
        } catch (error) {
            console.error('Failed to update qualification:', error);
            toast.error('更新に失敗しました');
        }
    };

    const filteredQualifications = qualifications.filter(q => {
        const term = searchTerm.toLowerCase();
        return (
            (q.user_name || '').toLowerCase().includes(term) ||
            (q.user_email || '').toLowerCase().includes(term) ||
            (q.qualification_name || '').toLowerCase().includes(term)
        );
    });

    const openModal = async (id) => {
        // If backend supports detail endpoint, use it. Otherwise use list item.
        // routes_qualifications.py has detail endpoint.
        try {
            const headers = { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` };
            const res = await axios.get(`${API}/admin/qualifications/${id}`, { headers });
            setSelectedQualification(res.data);
            setRejectionReason(res.data.rejection_reason || '');
            setShowModal(true);
        } catch (e) {
            toast.error('詳細の取得に失敗しました');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading && qualifications.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <button
                            onClick={() => window.location.href = '/admin-dashboard'}
                            className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">保有資格管理</h1>
                            <p className="text-gray-600 mt-1">求職者から提出された資格証明を審査します</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="ユーザー名、資格名で検索..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="sm:w-48">
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">すべてのステータス</option>
                            <option value="pending">確認中</option>
                            <option value="approved">承認済</option>
                            <option value="rejected">再提出要</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">資格名</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">取得日</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">提出日</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredQualifications.map((q) => (
                                <tr key={q.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{q.user_name}</div>
                                        <div className="text-sm text-gray-500">{q.user_email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center">
                                            <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            {q.qualification_name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {q.obtained_date || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[q.status || 'pending']}`}>
                                            {STATUS_LABELS[q.status || 'pending']}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(q.submitted_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => openModal(q.id)}
                                            className="text-blue-600 hover:text-blue-900 flex items-center"
                                        >
                                            <EyeIcon className="h-4 w-4 mr-1" />
                                            詳細
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredQualifications.length === 0 && !loading && (
                        <div className="text-center py-12 text-gray-500">
                            該当するデータがありません
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && selectedQualification && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">資格詳細審査</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Info */}
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                                <div><span className="text-gray-500">ユーザー:</span> <span className="font-medium ml-2">{selectedQualification.user_name}</span></div>
                                <div><span className="text-gray-500">資格名:</span> <span className="font-medium ml-2">{selectedQualification.qualification_name}</span></div>
                                <div><span className="text-gray-500">取得日:</span> <span className="font-medium ml-2">{selectedQualification.obtained_date || '-'}</span></div>
                                <div><span className="text-gray-500">提出日:</span> <span className="font-medium ml-2">{formatDate(selectedQualification.submitted_at)}</span></div>
                            </div>

                            {/* Image */}
                            <div>
                                <h4 className="font-medium text-gray-900 mb-2">証明書画像</h4>
                                {selectedQualification.qualification_image ? (
                                    <div className="border border-gray-200 rounded-lg p-2">
                                        <img
                                            src={selectedQualification.qualification_image}
                                            alt="Certificate"
                                            className="w-full rounded h-auto max-h-96 object-contain"
                                        />
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">画像なし</p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="border-t pt-4">
                                <h4 className="font-medium text-gray-900 mb-2">審査アクション</h4>
                                {selectedQualification.status === 'pending' || selectedQualification.status === 'rejected' ? (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">差戻し理由 (却下時のみ)</label>
                                            <textarea
                                                className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                                                rows={2}
                                                placeholder="画像が不鮮明です、など"
                                                value={rejectionReason}
                                                onChange={(e) => setRejectionReason(e.target.value)}
                                            />
                                        </div>
                                        <div className="flex gap-4">
                                            <button
                                                onClick={() => handleReview('approved')}
                                                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 flex items-center justify-center"
                                            >
                                                <CheckCircleIcon className="w-5 h-5 mr-2" />
                                                承認する
                                            </button>
                                            <button
                                                onClick={() => handleReview('rejected')}
                                                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 flex items-center justify-center"
                                            >
                                                <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                                                差し戻す
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-green-50 p-4 rounded-lg text-center">
                                        <CheckCircleIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                        <p className="text-green-800 font-medium">承認済みです</p>
                                        <button
                                            onClick={() => handleReview('rejected')}
                                            className="text-sm text-red-600 hover:underline mt-2"
                                        >
                                            承認を取り消して差し戻す
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminQualificationsPage;
